import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { task } from 'ember-concurrency';
import { TrackedAsyncData } from 'ember-async-data';
import YupValidations from '../../classes/yup-validations';
import { string } from 'yup';
import { and } from 'ember-truth-helpers';
import EditableField from '../editable-field';
import perform from 'ember-concurrency/helpers/perform';
import t from 'ember-intl/helpers/t';
import { on } from '@ember/modifier';
import { pageTitle } from 'ember-page-title';
import pick from '../../helpers/pick';
import set from 'ember-set-helper/helpers/set';
import YupValidationMessage from '../yup-validation-message';
import SubjectCopy from './subject-copy';
import SubjectDownload from './subject-download';
import SubjectYearFilter from './subject-year-filter';
import SubjectDescription from './subject-description';
import focus from '../../modifiers/focus';

export default class ReportsSubjectHeaderComponent extends Component {
  @service router;
  @service reporting;
  @tracked title = this.args.report?.title ?? '';

  validations = new YupValidations(this, {
    title: string().max(240),
  });

  @cached
  get reportTitleData() {
    return new TrackedAsyncData(
      this.reporting.buildReportTitle(
        this.args.subject,
        this.args.prepositionalObject,
        this.args.prepositionalObjectTableRowId,
        this.args.school,
      ),
    );
  }

  get reportTitle() {
    if (this.args.report?.title) {
      return this.args.report.title;
    }

    return this.reportTitleData.isResolved ? this.reportTitleData.value : null;
  }

  get isReportDetail() {
    return this.router.currentRouteName == 'reports.subject';
  }

  changeTitle = task({ drop: true }, async () => {
    this.validations.addErrorDisplayForAllFields();
    const isValid = await this.validations.isValid();
    if (!isValid) {
      return false;
    }
    this.validations.clearErrorDisplay();
    this.args.report.title = this.title;
    await this.args.report.save();
  });

  @action
  revertTitleChanges() {
    this.title = this.reportTitle;
  }
  <template>
    {{pageTitle " | " this.reportTitle prepend=false}}

    <div class="reports-subject-header" data-test-reports-subject-header>
      {{#if (and @report this.reportTitleData.isResolved)}}
        <h2 data-test-report-title>
          <EditableField
            data-test-title
            @value={{this.reportTitle}}
            @save={{perform this.changeTitle}}
            @close={{this.revertTitleChanges}}
            as |keyboard isSaving|
          >
            <input
              aria-label={{t "general.reportTitle"}}
              type="text"
              value={{this.reportTitle}}
              placeholder={{this.reportTitleData.value}}
              disabled={{isSaving}}
              {{on "input" (pick "target.value" (set this "title"))}}
              {{this.validations.attach "title"}}
              {{keyboard}}
              {{focus}}
            />
            <YupValidationMessage
              @description={{t "general.title"}}
              @validationErrors={{this.validations.errors.title}}
              data-test-title-validation-error-message
            />
          </EditableField>
        </h2>
      {{/if}}
      {{#if this.isReportDetail}}
        <SubjectCopy
          @report={{@report}}
          @subject={{@subject}}
          @prepositionalObject={{@prepositionalObject}}
          @prepositionalObjectTableRowId={{@prepositionalObjectTableRowId}}
          @school={{@school}}
        />
      {{/if}}
      <SubjectDownload
        @report={{@report}}
        @subject={{@subject}}
        @prepositionalObject={{@prepositionalObject}}
        @prepositionalObjectTableRowId={{@prepositionalObjectTableRowId}}
        @school={{@school}}
        @fetchDownloadData={{@fetchDownloadData}}
        @readyToDownload={{@readyToDownload}}
      />
      {{#if @showYearFilter}}
        <SubjectYearFilter @selectedYear={{@year}} @changeYear={{@changeYear}} />
      {{/if}}
      <SubjectDescription @description={{@description}} @resultsLength={{@resultsLength}} />
    </div>
  </template>
}
